import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Plus, Minus, Phone, ArrowUpRight, X } from 'lucide-react';
// --- Constants ---
const PHONE_DISPLAY = '+38 (068) 135-04-36';
const PHONE_HREF = 'tel:+380681350436';
// --- Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const AccordionItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-neutral-200" data-id="element-1">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-2">
        <span className="text-lg md:text-xl font-medium text-neutral-900 group-hover:text-neutral-600 transition-colors" data-id="element-3">
          {question}
        </span>
        <span className="ml-4 flex-shrink-0 text-neutral-400 group-hover:text-neutral-900 transition-colors" data-id="element-4">
          {isOpen ? <Minus size={20} data-id="element-5" /> : <Plus size={20} data-id="element-6" />}
        </span>
      </button>
      <AnimatePresence data-id="element-7">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-8">
            <p className="pb-6 text-neutral-600 leading-relaxed max-w-3xl" data-id="element-9">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
const PromoPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (hasShown) return;
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercentage = scrollPosition / (documentHeight - windowHeight) * 100;
      if (scrollPercentage >= 50) {
        setIsVisible(true);
        setHasShown(true);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasShown]);
  return <AnimatePresence data-id="element-10">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} transition={{
      duration: 0.4,
      ease: [0.21, 0.47, 0.32, 0.98]
    }} className="fixed bottom-6 right-6 z-50 w-[calc(100%-3rem)] md:w-[380px] bg-white border border-black shadow-2xl p-6" data-id="element-11">
          <button onClick={() => setIsVisible(false)} className="absolute top-4 right-4 text-neutral-400 hover:text-black transition-colors" data-id="element-12">
            <X size={20} data-id="element-13" />
          </button>

          <div className="mb-6" data-id="element-14">
            <h3 className="font-medium text-black text-lg uppercase tracking-tight mb-2" data-id="element-15">
              Сподобався дизайн?
            </h3>
            <div className="h-[1px] w-12 bg-black mb-4" data-id="element-16"></div>
            <p className="text-neutral-600 text-sm leading-relaxed" data-id="element-17">
              Ми можемо розробити такий сайт для вас повністю під ключ,
              адаптувати під усі ваші потреби. Вартість сайту під ключ —{' '}
              <span className="text-black font-medium" data-id="element-18">лише 8 000 грн</span>.
            </p>
          </div>

          <div className="flex flex-col gap-2" data-id="element-19">
            <a href="tel:+380508399309" className="flex items-center justify-between w-full px-4 py-3 bg-black text-white text-sm font-medium hover:bg-neutral-800 transition-colors" data-id="element-20">
              <span data-id="element-21">+380 50 839 93 09</span>
              <Phone size={14} data-id="element-22" />
            </a>
            <a href="https://jetly.agency/ua" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between w-full px-4 py-3 bg-white text-black border border-neutral-200 text-sm font-medium hover:border-black transition-colors" data-id="element-23">
              <span data-id="element-24">jetly.agency/ua</span>
              <ArrowUpRight size={14} data-id="element-25" />
            </a>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
// --- Main Page ---
export function App() {
  const services = [{
    id: '01',
    title: 'Питання ТЦК та СП',
    description: 'Повний юридичний супровід у територіальних центрах комплектування. Допомога в оформленні законної відстрочки від мобілізації, оскарження неправомірних дій, бездіяльності та рішень посадових осіб.'
  }, {
    id: '02',
    title: 'Військово-лікарська комісія (ВЛК)',
    description: 'Консультації щодо законності проходження ВЛК. Оскарження висновків комісії до Центральної ВЛК або в судовому порядку. Направлення на незалежне медичне обстеження.'
  }, {
    id: '03',
    title: 'Кримінальний захист (СЗЧ)',
    description: 'Ефективний адвокатський захист у кримінальних провадженнях щодо самовільного залишення частини (СЗЧ), дезертирства, непокори. Допомога у знятті з розшуку та представництво в ДБР.'
  }, {
    id: '04',
    title: 'Звільнення зі служби',
    description: "Юридичний супровід процесу звільнення з лав Збройних Сил України за станом здоров'я, сімейними обставинами, досягненням граничного віку або іншими законними підставами."
  }, {
    id: '05',
    title: 'Адміністративні справи (ст. 130)',
    description: 'Захист ваших прав у справах про адміністративні правопорушення. Професійне представництво в суді за ст. 130 КУпАП та іншими статтями, що стосуються військовослужбовців.'
  }, {
    id: '06',
    title: 'Виплати та пільги',
    description: 'Допомога в отриманні належного грошового забезпечення, бойових виплат, стягнення заборгованості, компенсацій за поранення (травму, контузію) та оформлення статусу УБД.'
  }];
  const faqs = [{
    question: 'Чи працюєте ви дистанційно з військовослужбовцями, які перебувають у зоні бойових дій?',
    answer: 'Так, ми надаємо повноцінну юридичну допомогу дистанційно. Ви можете надіслати необхідні документи через месенджери (Signal, Telegram, WhatsApp), і ми оперативно почнемо роботу над вашою справою без вашої особистої присутності.'
  }, {
    question: 'Скільки коштує первинна консультація?',
    answer: 'Вартість первинної консультації залежить від складності вашого питання та обсягу документів, які необхідно проаналізувати. Під час першого звернення ми зорієнтуємо вас щодо точної вартості.'
  }, {
    question: 'Які гарантії ви надаєте у кримінальних справах (СЗЧ)?',
    answer: 'Адвокатська етика забороняє гарантувати 100% результат у суді. Проте ми гарантуємо професійний підхід, використання всіх законних механізмів захисту, повну конфіденційність та чесну оцінку перспектив вашої справи на самому початку.'
  }, {
    question: 'Як швидко ви можете підготувати рапорт або скаргу?',
    answer: 'У термінових випадках ми готуємо необхідні документи (рапорти, адвокатські запити, скарги) протягом 24 годин з моменту надання вами всієї необхідної інформації.'
  }];
  return <div className="min-h-screen bg-white font-sans text-black selection:bg-black selection:text-white overflow-x-hidden" data-id="element-26">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-sm z-40 border-b border-neutral-200" data-id="element-27">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between" data-id="element-28">
          <div className="font-medium tracking-tight uppercase text-sm md:text-base" data-id="element-29">
            Військовий Адвокат
          </div>
          <div className="flex items-center gap-6" data-id="element-30">
            <a href={PHONE_HREF} className="hidden md:block text-sm font-medium hover:text-neutral-500 transition-colors" data-id="element-31">
              {PHONE_DISPLAY}
            </a>
            <a href="#contact" className="px-5 py-2.5 bg-black text-white text-sm font-medium hover:bg-neutral-800 transition-colors" data-id="element-32">
              Консультація
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-20 md:pt-52 md:pb-32 px-6 max-w-7xl mx-auto" data-id="element-33">
        <FadeIn data-id="element-34">
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.05] font-medium tracking-tighter uppercase max-w-5xl mb-8 break-words" data-id="element-35">
            Захист прав військовослужбовців та призовників.
          </h1>
        </FadeIn>

        <div className="grid md:grid-cols-2 gap-8 md:gap-16 mt-12 md:mt-24" data-id="element-36">
          <FadeIn delay={0.1} data-id="element-37">
            <p className="text-xl md:text-2xl text-neutral-600 leading-snug font-light max-w-lg" data-id="element-38">
              Професійна юридична допомога у складних ситуаціях. Від
              консультацій щодо ВЛК та ТЦК до захисту в кримінальних справах.
            </p>
          </FadeIn>
          <FadeIn delay={0.2} className="flex flex-col sm:flex-row gap-4 md:justify-end items-start" data-id="element-39">
            <a href={PHONE_HREF} className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-black text-white font-medium hover:bg-neutral-800 transition-colors w-full sm:w-auto" data-id="element-40">
              Зателефонувати
              <ArrowUpRight size={18} data-id="element-41" />
            </a>
            <a href="#services" className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-black border border-neutral-200 font-medium hover:border-black transition-colors w-full sm:w-auto" data-id="element-42">
              Послуги
            </a>
          </FadeIn>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32 bg-neutral-50 border-y border-neutral-200 px-6" data-id="element-43">
        <div className="max-w-7xl mx-auto" data-id="element-44">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8" data-id="element-45">
            <div className="lg:col-span-4" data-id="element-46">
              <FadeIn data-id="element-47">
                <h2 className="text-sm font-medium uppercase tracking-widest text-neutral-500 mb-4" data-id="element-48">
                  Про адвоката
                </h2>
                <div className="h-[1px] w-12 bg-black" data-id="element-49"></div>
              </FadeIn>
            </div>
            <div className="lg:col-span-8" data-id="element-50">
              <FadeIn delay={0.1} data-id="element-51">
                <h3 className="text-2xl md:text-4xl font-medium leading-tight mb-8" data-id="element-52">
                  Забезпечуємо надійний правовий тил для військовослужбовців та
                  призовників.
                </h3>
                <div className="grid sm:grid-cols-2 gap-6 text-lg text-neutral-600 font-light" data-id="element-53">
                  <div className="border-l border-neutral-200 pl-4" data-id="element-54">
                    <strong className="block text-black font-medium mb-1" data-id="element-55">
                      Реальний досвід
                    </strong>
                    Взаємодія з ТЦК, ВЛК, ДБР, ВСП та судами всіх інстанцій.
                  </div>
                  <div className="border-l border-neutral-200 pl-4" data-id="element-56">
                    <strong className="block text-black font-medium mb-1" data-id="element-57">
                      Чіткий алгоритм
                    </strong>
                    Не даємо порожніх обіцянок — пропонуємо дієву стратегію
                    захисту.
                  </div>
                </div>
                <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-8 border-t border-neutral-200 pt-12" data-id="element-58">
                  <div data-id="element-59">
                    <div className="text-3xl md:text-4xl font-medium mb-2" data-id="element-60">
                      24/7
                    </div>
                    <div className="text-xs md:text-sm text-neutral-500 uppercase tracking-wide" data-id="element-61">
                      Підтримка клієнтів
                    </div>
                  </div>
                  <div data-id="element-62">
                    <div className="text-3xl md:text-4xl font-medium mb-2" data-id="element-63">
                      100%
                    </div>
                    <div className="text-xs md:text-sm text-neutral-500 uppercase tracking-wide" data-id="element-64">
                      Конфіденційність
                    </div>
                  </div>
                  <div className="col-span-2 sm:col-span-1" data-id="element-65">
                    <div className="text-3xl md:text-4xl font-medium mb-2" data-id="element-66">
                      Вся
                    </div>
                    <div className="text-xs md:text-sm text-neutral-500 uppercase tracking-wide" data-id="element-67">
                      Територія України
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 px-6 max-w-7xl mx-auto" id="services" data-id="element-68">
        <FadeIn data-id="element-69">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 md:mb-24 gap-8" data-id="element-70">
            <div data-id="element-71">
              <h2 className="text-sm font-medium uppercase tracking-widest text-neutral-500 mb-4" data-id="element-72">
                Експертиза
              </h2>
              <h3 className="text-4xl md:text-5xl font-medium tracking-tight" data-id="element-73">
                Юридичні послуги
              </h3>
            </div>
            <p className="text-neutral-600 max-w-md text-lg font-light" data-id="element-74">
              Комплексний підхід до вирішення правових питань
              військовослужбовців та військовозобов'язаних.
            </p>
          </div>
        </FadeIn>

        <div className="border-t border-black" data-id="element-75">
          {services.map((service, index) => <FadeIn key={index} delay={index * 0.05} data-id="element-76">
              <div className="group border-b border-neutral-200 py-8 md:py-12 flex flex-col lg:flex-row gap-4 lg:gap-12 hover:bg-neutral-50 transition-colors" data-id="element-77">
                <div className="text-neutral-400 font-medium text-lg w-12 flex-shrink-0" data-id="element-78">
                  {service.id}
                </div>
                <div className="lg:w-1/3 flex-shrink-0" data-id="element-79">
                  <h4 className="text-2xl md:text-3xl font-medium group-hover:translate-x-2 transition-transform duration-300" data-id="element-80">
                    {service.title}
                  </h4>
                </div>
                <div className="lg:w-auto" data-id="element-81">
                  <p className="text-neutral-600 text-base md:text-lg font-light leading-relaxed max-w-2xl" data-id="element-82">
                    {service.description}
                  </p>
                </div>
              </div>
            </FadeIn>)}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 bg-black text-white px-6" data-id="element-83">
        <div className="max-w-7xl mx-auto" data-id="element-84">
          <FadeIn data-id="element-85">
            <h2 className="text-sm font-medium uppercase tracking-widest text-neutral-400 mb-4" data-id="element-86">
              Як ми працюємо
            </h2>
            <h3 className="text-4xl md:text-5xl font-medium tracking-tight mb-16 md:mb-24" data-id="element-87">
              Етапи співпраці
            </h3>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-12 md:gap-8" data-id="element-88">
            {[{
            num: '01',
            title: 'Первинна консультація',
            desc: 'Детальний аналіз вашої ситуації, вивчення наявних документів та оцінка правових ризиків і перспектив.'
          }, {
            num: '02',
            title: 'Побудова стратегії',
            desc: 'Розробка покрокового плану дій, підготовка необхідних рапортів, заяв, адвокатських запитів та скарг.'
          }, {
            num: '03',
            title: 'Юридичний супровід',
            desc: 'Представництво ваших інтересів у державних органах, ТЦК, ВЛК, правоохоронних органах та судах до досягнення результату.'
          }].map((step, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-89">
                <div className="border-t border-neutral-800 pt-8" data-id="element-90">
                  <div className="text-5xl font-light text-neutral-600 mb-6" data-id="element-91">
                    {step.num}
                  </div>
                  <h4 className="text-2xl font-medium mb-4" data-id="element-92">{step.title}</h4>
                  <p className="text-neutral-400 font-light leading-relaxed" data-id="element-93">
                    {step.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32 px-6 max-w-3xl mx-auto" data-id="element-94">
        <FadeIn data-id="element-95">
          <div className="text-center mb-16" data-id="element-96">
            <h2 className="text-sm font-medium uppercase tracking-widest text-neutral-500 mb-4" data-id="element-97">
              Інформація
            </h2>
            <h3 className="text-4xl md:text-5xl font-medium tracking-tight" data-id="element-98">
              Часті питання
            </h3>
          </div>
        </FadeIn>

        <div className="border-t border-black" data-id="element-99">
          {faqs.map((faq, index) => <FadeIn key={index} delay={index * 0.1} data-id="element-100">
              <AccordionItem question={faq.question} answer={faq.answer} data-id="element-101" />
            </FadeIn>)}
        </div>
      </section>

      {/* Footer / Contact */}
      <footer className="bg-neutral-50 border-t border-neutral-200 pt-24 pb-12 px-6" id="contact" data-id="element-102">
        <div className="max-w-7xl mx-auto" data-id="element-103">
          <div className="grid lg:grid-cols-2 gap-12 md:gap-16 mb-16 md:mb-24" data-id="element-104">
            <div data-id="element-105">
              <FadeIn data-id="element-106">
                <h2 className="text-4xl sm:text-5xl md:text-7xl font-medium tracking-tighter uppercase mb-8 break-words" data-id="element-107">
                  Почати
                  <br data-id="element-108" />
                  роботу.
                </h2>
                <p className="text-lg md:text-xl text-neutral-600 font-light max-w-md" data-id="element-109">
                  Зв'яжіться з нами для отримання фахової правової допомоги. Ваш
                  час має значення.
                </p>
              </FadeIn>
            </div>

            <div className="flex flex-col lg:items-end justify-center" data-id="element-110">
              <FadeIn delay={0.1} data-id="element-111">
                <div className="mb-8 lg:text-right" data-id="element-112">
                  <div className="text-sm font-medium uppercase tracking-widest text-neutral-500 mb-2" data-id="element-113">
                    Телефон
                  </div>
                  <a href={PHONE_HREF} className="text-3xl md:text-5xl font-medium hover:text-neutral-500 transition-colors" data-id="element-114">
                    {PHONE_DISPLAY}
                  </a>
                </div>

                <div className="lg:text-right" data-id="element-115">
                  <div className="text-sm font-medium uppercase tracking-widest text-neutral-500 mb-4" data-id="element-116">
                    Месенджери
                  </div>
                  <div className="flex flex-wrap gap-4 lg:justify-end" data-id="element-117">
                    <a href="#" className="px-6 py-3 bg-black text-white text-sm font-medium hover:bg-neutral-800 transition-colors flex items-center gap-2" data-id="element-118">
                      Telegram <ArrowUpRight size={16} data-id="element-119" />
                    </a>
                    <a href="#" className="px-6 py-3 bg-white text-black border border-neutral-200 text-sm font-medium hover:border-black transition-colors flex items-center gap-2" data-id="element-120">
                      Viber <ArrowUpRight size={16} data-id="element-121" />
                    </a>
                    <a href="#" className="px-6 py-3 bg-white text-black border border-neutral-200 text-sm font-medium hover:border-black transition-colors flex items-center gap-2" data-id="element-122">
                      WhatsApp <ArrowUpRight size={16} data-id="element-123" />
                    </a>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>

          <div className="pt-8 border-t border-neutral-200 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-neutral-500 font-light" data-id="element-124">
            <p data-id="element-125">
              © {new Date().getFullYear()} Військовий Адвокат. Всі права
              захищені.
            </p>
            <p data-id="element-126">Конфіденційність гарантовано.</p>
          </div>
        </div>
      </footer>

      {/* Scroll Triggered Popup */}
      <PromoPopup data-id="element-127" />
    </div>;
}